import type { ParsedDictationChunk } from '../createNvoqWebsocketFactory';
import type { Editor } from '../../../../core';
import {
  extractTextFromItem,
  getLastWord,
  createTextEndsChecker,
  isText,
  maybeEndOfSentence,
} from './textUtils';
import { getCurrentListItemPosition } from '../../../list/utils';

// Common radiology measurement units
// Size/Volume: cm, mm, ml
// Weight: kg, g, mg
// PET Scan Metrics: suv
export const RADIOLOGY_UNITS = ['cm', 'mm', 'ml', 'kg', 'g', 'mg', 'suv'];

// Regex to detect **complete** decimal numbers (e.g., "1.2", "3.14", "0.5")
// Used to prevent numbered list creation for medical measurements
// edge case is if there is a decimal in the chunk but starts with a number list match
// i.e. "1.CT angiogram 2.4 cm space" should still start a list so we need to start with a digit
export const COMPLETE_DECIMAL_NUMBER_REGEX = /^\d+\.\d+/;

/**
 * Regex to detect decimal numbers **anywhere** in text (not just at the start)
 * Used to prevent numbered list creation when decimal numbers are present
 */
export const CONTAINS_DECIMAL_NUMBER_REGEX = /\d+\.\d+/;

/**
 * Checks if text contains a complete decimal number at the start
 */
export const startsWithCompleteDecimal = (text: string): boolean => {
  return COMPLETE_DECIMAL_NUMBER_REGEX.test(text);
};

/**
 * Checks if text contains any decimal number anywhere
 */
export const hasDecimalNumber = (text: string): boolean => {
  return CONTAINS_DECIMAL_NUMBER_REGEX.test(text);
};

/**
 * Removes spaces within decimal numbers
 * e.g., "1 . 2" -> "1.2", "2 . 1 x 1 . 7" -> "2.1 x 1.7"
 */
export const removeDecimalSpacing = (text: string): string => {
  // Pattern matches: digit + optional spaces + period + optional spaces + digit
  // This handles cases like "1 . 2", "1. 2", "1 .2", etc.
  return text.replace(/(\d)\s*\.\s*(\d)/g, '$1.$2');
};

/**
 * Adds space between numbers with/ unit measurements i.e. "1.2cm" -> "1.2 cm", "2.1mm" -> "2.1 mm", "5cm" -> "5 cm"ithout
 * Also adds spaces around "x" operator i.e. "4.5x1.2" -> "4.5 x 1.2"
 * Edge case
 */
export const addSpacingAfterDecimals = (text: string): string => {
  const unitsPattern = RADIOLOGY_UNITS.join('|');

  // Regex to match number + unit, allowing 'x' after but blocking other letters
  // This preserves "5th" while allowing "2.1cmx1.7mm" to be processed
  const numberWithUnitRegex = new RegExp(`(\\d+\\.?\\d*)(${unitsPattern})(?![a-wy-z])`, 'gi');

  // Regex to match any character + 'x' + digit (for x-spacing)
  const xBetweenCharAndDigitRegex = /(\S)\s*x\s*(\d)/gi;

  const maxPasses = 10; // Assuming max 40 chars, 10 passes handles all realistic cases

  // Step 1: Add space between numbers and radiology measurement units FIRST
  // i.e. "2.1cmx1.7mm" -> "2.1 cm x 1.7 mm" (separates units from numbers)
  let result = text.replace(numberWithUnitRegex, '$1 $2');

  // Step 2: Add proper spacing around x when it's between any non-whitespace and a digit
  // Handles "cm x 1" and "5 x 1", use multiple passes to handle chains like "1.5x1.7x2.1"
  for (let i = 0; i < maxPasses; i++) {
    result = result.replace(xBetweenCharAndDigitRegex, '$1 x $2');
  }

  return result;
};

/**
 * Normalizes decimal number spacing by removing internal spaces and adding unit spacing
 * e.g., "1 . 2cm" -> "1.2 cm", "2 . 1 x 1 . 7mm" -> "2.1 x 1.7 mm"
 */
export const normalizeDecimalSpacing = (text: string): string => {
  return addSpacingAfterDecimals(removeDecimalSpacing(text));
};

// Regex for numbered list patterns (e.g., "1.", "#2")
// This regex will NOT match "1." if it's followed by a digit (like in "1.2")
export const NUMBERED_LIST_REGEX = /(?:^|\s)(\d+\.(?![.\d\w])|#\d+(?![.\w]))/;

// Regex to match "number" (case-insensitive) as a whole word or "#" as a standalone symbol.
// Uses non-capturing group to check for:
// - "\bnumber\b": "number" surrounded by word boundaries to ensure it's not part of a larger word.
// - "(?<!\S)#(?!\S)": "#" not preceded or followed by any non-whitespace characters, ensuring it's isolated.
export const NUMBER_KEYWORD_REGEX = /(?:\bnumber\b|(?<!\S)#(?!\S))/i;

export const DIGIT_REGEX = /(?<!\S)\d+(?!\S)/;

/**
 * Regex to match "number" + digit pattern anywhere in text (e.g., "number 2", "number 3")
 * Used to detect list continuation commands.
 * Note: This is different from NUMBER_KEYWORD_REGEX which only matches the keyword alone.
 * The negative lookahead ensures we don't match decimals (e.g., "number 2.5" should not match).
 */
export const NUMBER_DIGIT_PATTERN_REGEX = /\bnumber\s+\d+(?!\.)/i;

/**
 * Checks if a dictation chunk ends in a number keyword ("number" or "#")
 */
export const isTextAndEndsInNextNumberKeyword = createTextEndsChecker((lastWord) =>
  NUMBER_KEYWORD_REGEX.test(lastWord)
);

/**
 * Checks if a dictation chunk ends in a digit
 */
export const isTextAndEndsInDigit = createTextEndsChecker((lastWord) => DIGIT_REGEX.test(lastWord));

/**
 * Checks if text ends with a period (for decimal number handling)
 */
export const endsWithPeriod = (text: string): boolean => {
  return text.trim().endsWith('.');
};

/**
 * Checks if a dictation chunk ends in a digit that represents the next number in a sequence
 * Used for numbered list continuation logic
 */
export const endsInDigitAndIsNextNumber = (item: ParsedDictationChunk, editor: Editor): boolean => {
  const text = extractTextFromItem(item);
  if (!text) return false;

  // Check if the text ends with a digit
  const lastWord = getLastWord(text);
  if (!lastWord || !DIGIT_REGEX.test(lastWord)) return false;

  const listPosition = getCurrentListItemPosition(editor, editor.selection);
  const digitAsNumber = parseInt(lastWord, 10);

  if (listPosition === -1) {
    // If the list position is -1, it means the user is not currently in a list item
    // If the digit is not 1, it means the user is not in a numbered list
    // In this case, we can assume that the digit is not a valid list item number
    return digitAsNumber === 1;
  }

  // If we reached this point, it means the digit is valid and the user is in a list item
  // We can now check if the digit corresponds to the current list item position
  // e.g. if we're at list item 0 (the 1. list item), then only "2" is valid
  // e.g. if we're at list item 5 (the 6. list item), then only "7" is valid
  return digitAsNumber === listPosition + 2;
};

/**
 * Checks if text contains a "number" + digit pattern (not "#2") that should continue a list
 * should not wait/defer to check if it might be a decimal.
 *
 */
export const containsNumberDigitListPattern = (text: string): boolean => {
  if (!text) return false;

  return NUMBER_DIGIT_PATTERN_REGEX.test(text);
};

/**
 * Check if text ends might be a decimal based on a numbered list pattern.
 * Helper function for isTextAndMaybeDecimalPattern
 */
const checkTextForMaybeDecimalPattern = (processedText: string): boolean => {
  // Check if text ends with a numbered list pattern
  const match = NUMBERED_LIST_REGEX.exec(processedText);
  if (!match) return false;

  // Check if the pattern is at the end of the text
  const matchEnd = match.index + match[0].length;
  const textAfterMatch = processedText.slice(matchEnd).trim();

  if (textAfterMatch.length > 0) {
    // There's text after the pattern, not ambiguous
    return false;
  }

  // Check if it's a digit followed by period (ambiguous case)
  // Pattern like "1.", "2.", "10.", "25." but not "#1"
  const trimmedMatch = match[0].trim();
  const digitPeriodPattern = /^\d+\.$/;

  if (!digitPeriodPattern.test(trimmedMatch)) {
    return false;
  }

  // // Pattern must be at the start OR preceded by sentence-ending punctuation
  // // This prevents "Nodule labeled 1." from being ambiguous
  const textBeforeMatch = processedText.slice(0, match?.index ?? 0);
  // // we should maybe check if there is a new line before the pattern (this would be checked else where)
  const isAtStart = match.index === 0;

  return isAtStart || maybeEndOfSentence(textBeforeMatch);
};

/**
 * Detects if a dictation chunk ends with a pattern that might be a decimal.
 * This is used for the deferred approach where "1." could be a list or start of "1.2".
 *
 * Returns true if:
 * - Text ends with a numbered list pattern (like "1.", "2.")
 * - Pattern is a single digit followed by period
 * - No text follows the pattern
 * - Pattern is at start OR preceded by sentence ending punctuation
 */
export const isTextAndMaybeDecimalPattern = (
  item: ParsedDictationChunk,
  processedText?: string
): boolean => {
  if (!isText(item) || !(item.payload as any).data?.text) {
    return false;
  }

  const text = extractTextFromItem(item);
  if (!text) return false;

  // Use provided processedText if available (to avoid circular dependency)
  // Otherwise just use the raw text (won't catch similar phrases like "one period")
  const textToCheck = processedText ?? text;

  return checkTextForMaybeDecimalPattern(textToCheck);
};

export const isSubstitutionStartNumberingWithDecimal = (item: ParsedDictationChunk): boolean => {
  if (item.type === 'SUBSTITUTION' && item.payload.target === 'EDITOR') {
    const payload = item.payload as any;
    if (
      (payload.action === 'StartNumbering' || payload.action === 'NextNumberDynamic') &&
      payload.originalTextStepEntry &&
      Array.isArray(payload.originalTextStepEntry)
    ) {
      // Check if the original text was "1." or "2." which could be a decimal
      const originalChunk = payload.originalTextStepEntry[1];
      return isTextAndMaybeDecimalPattern(originalChunk);
    }
  }
  return false;
};

/**
 * Checks if a new chunk would form a decimal with a previous maybe-decimal pattern.
 *
 * Example:
 * - Previous: "1."
 * - New: "2 cm"
 * - Result: true (would form "1.2 cm")
 */
export const wouldFormDecimal = (newChunkText: string): boolean => {
  if (!newChunkText) return false;

  // Check if the new text starts with a digit (possibly with leading space)
  const startsWithDigit = /^\s*\d/.test(newChunkText);

  return startsWithDigit;
};

/**
 * Combines a maybe-decimal pattern with new text to form a complete decimal.
 * Ensures proper spacing after the decimal.
 *
 * Example:
 * - ambiguousText: "1."
 * - newText: "2 cm"
 * - Result: "1.2 cm"
 * - newText: "2 hello"
 * - Result: "1.2 hello" (space preserved)
 */
export const combineIntoDecimal = (ambiguousText: string, newText: string): string => {
  // Trim the ambiguous text to handle trailing spaces
  const trimmedAmbiguous = ambiguousText.trim();

  // Extract the digit(s) and period from ambiguous text
  const match = trimmedAmbiguous.match(/(\d+)\.$/);
  if (!match) return ambiguousText + ' ' + newText;

  const digit = match[1];

  // Extract the leading digit(s) and remainder from new text
  const trimmedNewText = newText.trim();
  const newMatch = trimmedNewText.match(/^(\d+)(.*)/);
  if (!newMatch) return ambiguousText + ' ' + newText;

  const newDigit = newMatch[1];
  const remainder = newMatch[2].trim(); // Trim to normalize spacing

  // Combine into decimal with proper spacing
  if (!remainder) {
    // No remainder, just the decimal
    return `${digit}.${newDigit}`;
  } else {
    // Text after decimal, add space
    return `${digit}.${newDigit} ${remainder}`;
  }
};
